import { c as _c } from "react/compiler-runtime";
import { Button, Input } from '@common/components';
import { useToast } from '@common/hooks';
import SvgChevronleft from '@common/icon/SvgChevronleft';
import SvgDelete from '@common/icon/SvgDelete';
import SvgExpandWindow from '@common/icon/SvgExpandWindow';
import SvgHome from '@common/icon/SvgHome';
import SvgKebab from '@common/icon/SvgKebab';
import SvgLogo from '@common/icon/SvgLogo';
import SvgMagnifyingGlass from '@common/icon/SvgMagnifyingGlass';
import SvgMyFill from '@common/icon/SvgMyFill';
import SvgNotification from '@common/icon/SvgNotification';
import SvgSetting from '@common/icon/SvgSetting';
import { cn } from '@core/utils';
import { type ReactNode, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { CustomLink } from '@/app/_components/CustomLink';
import { openShareDrawer, type ShareContent } from '@/app/_components/GlobalModal/_components/ShareDrawer';
import { shouldShowMobileTrendKeyword } from '@/app/_components/TrendKeyword/constants';
import { TrendKeywordBar } from '@/app/_components/TrendKeyword/TrendKeywordBar';
import { DONATION_ROUTES, MODULE_ROUTES, ROUTES, SELECTOR, SUB_DOMAIN } from '@/constants';
import { useCheckKakaobank } from '@/hooks/useCheckKakaobank';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { hasOverlayHistoryEntry } from '@/hooks/useOverlayHistory';
import { usePartnerChromeClassName } from '@/hooks/usePartnerChromeClassName';
import { usePublishElementHeight } from '@/hooks/usePublishElementHeight';
import { useRenderedPathname } from '@/hooks/useRenderedPathname';
import { KakaobankExitConfirmModal } from '@/modules/kakaobank/_components/KakaobankExitConfirmModal';
import { requestKakaobankClose } from '@/modules/kakaobank/_utils/appBridge';
import { KAKAOBANK_NAV_SUBTITLE, resolveKakaobankNavTitle } from '@/modules/kakaobank/_utils/navTitle';
import { TossExitConfirmModal, useTossWebView } from '@/modules/toss';
import type { FlutterInAppWebViewWindow } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { useKakaobankExitStore } from '@/stores/KakaobankExitStore';
import { useResponsiveDialogStore } from '@/stores/ResponsiveDialogStore';
import { findRoute } from '@/utils';
import { gtmDatasets } from '@/utils/analytics/gtm';
import { Image } from '../Image/Image';
const TOSS_LOGO_URL = 'https://cdn.welfarehello.com/static/toss-logo.png';
type NavigationRouter = Pick<ReturnType<typeof useCustomRouter>, 'push' | 'replace' | 'back'>;
export type PageNavigationContentProps = PageNavigationProps & {
  isToss: boolean;
  isKakaobank: boolean;
  isLogin?: boolean;
  pathname: string;
  router: NavigationRouter;
  openConfirmModal: VoidFunction;
  closeConfirmModal: VoidFunction;
  confirmAndClose: VoidFunction;
  isModalOpened: boolean;
};
const PageNavigationContent = t0 => {
  const $ = _c(152);
  const {
    logo,
    logoHref,
    title,
    titleLeft,
    children,
    backButton: t1,
    backButtonType: t2,
    searchButton,
    bellButton,
    notification,
    shareButton,
    shareContent,
    settingsButton,
    kebabButton,
    onClickBack,
    fallbackHref,
    onClickKebab,
    hideHomeButton,
    className,
    style,
    textColor,
    isNotUsedHomeButton,
    donationHomeButton,
    benefitHomeButton,
    searchForm,
    searchFormDefaultValue,
    searchFormClassName,
    onSearchFormMount,
    onSearchFormClick,
    onSearchFormSubmit,
    isToss,
    isKakaobank,
    isLogin,
    pathname,
    router,
    openConfirmModal,
    closeConfirmModal,
    confirmAndClose,
    isModalOpened
  } = t0;
  const backButton = t1 === undefined ? true : t1;
  const backButtonType = t2 === undefined ? "back" : t2;
  const partnerChromeClassName = usePartnerChromeClassName();
  const hasBottomNav = findRoute(pathname)?.hasBottomNav;
  const [isKakaobankExitOpened, openKakaobankExit, closeKakaobankExit] = useKakaobankExitStore(useShallow(_temp));
  const {
    toast
  } = useToast();
  let t3;
  if ($[0] !== closeKakaobankExit || $[1] !== toast) {
    t3 = () => {
      try {
        requestKakaobankClose();
      } catch {
        closeKakaobankExit();
        toast({
          type: "fail",
          message: "\uC9C0\uAE08\uC740 \uC885\uB8CC\uD560 \uC218 \uC5C6\uC5B4\uC694. \uCE74\uCE74\uC624\uBC45\uD06C \uC571\uC744 \uCD5C\uC2E0 \uBC84\uC804\uC73C\uB85C \uC5C5\uB370\uC774\uD2B8\uD574\uC8FC\uC138\uC694."
        });
      }
    };
    $[0] = closeKakaobankExit;
    $[1] = toast;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleKakaobankClose = t3;
  const DEFAULT_LOGO_HREF = isToss ? DONATION_ROUTES.HOME.pathname : ROUTES.HOME.pathname;
  let t4;
  if ($[3] !== fallbackHref || $[4] !== isKakaobank || $[5] !== onClickBack || $[6] !== router) {
    t4 = onClickBack ? typeof onClickBack === "string" ? () => router.replace(onClickBack) : onClickBack : () => router.back(isKakaobank ? DONATION_ROUTES.HOME.pathname : fallbackHref);
    $[3] = fallbackHref;
    $[4] = isKakaobank;
    $[5] = onClickBack;
    $[6] = router;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleBack = t4;
  let t5;
  if ($[8] !== fallbackHref || $[9] !== onClickBack || $[10] !== pathname) {
    t5 = typeof onClickBack === "string" ? onClickBack : fallbackHref ?? (pathname.startsWith(DONATION_ROUTES.HOME.pathname) ? DONATION_ROUTES.HOME.pathname : ROUTES.HOME.pathname);
    $[8] = fallbackHref;
    $[9] = onClickBack;
    $[10] = pathname;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const backFallbackHref = t5;
  if (isKakaobank) {
    let t6;
    if ($[12] !== pathname) {
      t6 = DONATION_ROUTES.HOME.isMatchPathname(pathname);
      $[12] = pathname;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    const isDonationHome = t6;
    let t7;
    if ($[14] !== pathname) {
      t7 = DONATION_ROUTES.CALLBACK.isMatchPathname(pathname);
      $[14] = pathname;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    const isDonationCallback = t7;
    let t8;
    if ($[16] !== pathname || $[17] !== title) {
      t8 = resolveKakaobankNavTitle(pathname) ?? title;
      $[16] = pathname;
      $[17] = title;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    const navTitle = t8;
    let t9;
    if ($[19] !== isLogin) {
      t9 = isLogin === false ? ROUTES.LOGIN.withSearchParams({
        searchParams: {
          redirect: DONATION_ROUTES.MY.pathname
        }
      }) : DONATION_ROUTES.MY.pathname;
      $[19] = isLogin;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    const myHref = t9;
    const kakaobankSearchHref = typeof searchButton === "string" ? searchButton : DONATION_ROUTES.SEARCH.pathname;
    let t10;
    bb0: {
      if (isDonationHome) {
        let t11;
        if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <SvgDelete className="size-24" />;
          $[21] = t11;
        } else {
          t11 = $[21];
        }
        let t12;
        if ($[22] !== openKakaobankExit) {
          t12 = {
            label: "\uCE74\uCE74\uC624\uBC45\uD06C\uB85C \uB3CC\uC544\uAC00\uAE30",
            icon: t11,
            onClick: openKakaobankExit
          };
          $[22] = openKakaobankExit;
          $[23] = t12;
        } else {
          t12 = $[23];
        }
        t10 = t12;
        break bb0;
      }
      if (isDonationCallback) {
        let t11;
        if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <SvgDelete className="size-24" />;
          $[24] = t11;
        } else {
          t11 = $[24];
        }
        let t12;
        if ($[25] !== handleBack) {
          t12 = {
            label: "\uB2EB\uAE30",
            icon: t11,
            onClick: handleBack
          };
          $[25] = handleBack;
          $[26] = t12;
        } else {
          t12 = $[26];
        }
        t10 = t12;
        break bb0;
      }
      if (backButton) {
        let t11;
        if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <SvgChevronleft className="size-24" />;
          $[27] = t11;
        } else {
          t11 = $[27];
        }
        let t12;
        if ($[28] !== handleBack) {
          t12 = {
            label: "\uB4A4\uB85C \uAC00\uAE30",
            icon: t11,
            onClick: handleBack
          };
          $[28] = handleBack;
          $[29] = t12;
        } else {
          t12 = $[29];
        }
        t10 = t12;
        break bb0;
      }
      t10 = null;
    }
    const leftAction = t10;
    const t11 = isDonationHome ? "pr-8" : "pr-16";
    let t12;
    if ($[30] !== className || $[31] !== partnerChromeClassName || $[32] !== t11) {
      t12 = cn("relative flex min-h-48 w-full items-center justify-between pl-4", t11, "box-border select-none bg-Gray-white text-center", partnerChromeClassName, className);
      $[30] = className;
      $[31] = partnerChromeClassName;
      $[32] = t11;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    let t13;
    if ($[34] !== leftAction) {
      t13 = leftAction ? <Button aria-label={leftAction.label} className="relative size-26 p-0 before:absolute before:-inset-9 before:content-['']" dimensions="H26" theme="primary-text" onClick={leftAction.onClick}>{leftAction.icon}</Button> : null;
      $[34] = leftAction;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t14;
    if ($[36] !== t13) {
      t14 = <div className="grid size-44 shrink-0 place-items-center">{t13}</div>;
      $[36] = t13;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    let t15;
    if ($[38] !== navTitle) {
      t15 = navTitle ? <h3 className="w-full truncate font-700 text-Gray-900 typo-body3">{navTitle}</h3> : null;
      $[38] = navTitle;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    let t16;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <p className="font-400 text-Gray-700 typo-caption1">{KAKAOBANK_NAV_SUBTITLE}</p>;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    let t17;
    if ($[41] !== t15) {
      t17 = <div className="absolute inset-x-0 mx-auto flex w-185 flex-col items-center gap-2 px-8">{t15}{t16}</div>;
      $[41] = t15;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    let t18;
    if ($[43] !== isDonationCallback || $[44] !== isDonationHome || $[45] !== kakaobankSearchHref || $[46] !== myHref || $[47] !== openKakaobankExit || $[48] !== searchButton) {
      t18 = isDonationHome ? <>{searchButton ? <div className="grid size-44 place-items-center"><Button aria-label={"\uAC80\uC0C9\uC73C\uB85C \uC774\uB3D9"} asChild={true} className="relative size-26 p-0 before:absolute before:-inset-9 before:content-['']" dimensions="H26" theme="primary-text"><CustomLink prefetch={false} href={kakaobankSearchHref}><SvgMagnifyingGlass className="size-24" /></CustomLink></Button></div> : null}<div className="grid size-44 place-items-center"><Button aria-label="MY" asChild={true} className="relative size-28 p-0 before:absolute before:-inset-8 before:content-['']" dimensions="H26" theme="primary-text"><CustomLink prefetch={false} href={myHref}><SvgMyFill className="size-28 shrink-0" /></CustomLink></Button></div></> : isDonationCallback ? null : <Button aria-label={"\uCE74\uCE74\uC624\uBC45\uD06C\uB85C \uB3CC\uC544\uAC00\uAE30"} className="relative h-26 whitespace-nowrap p-0 before:absolute before:-inset-9 before:content-['']" dimensions="H40" theme="primary-text" onClick={openKakaobankExit}>닫기</Button>;
      $[43] = isDonationCallback;
      $[44] = isDonationHome;
      $[45] = kakaobankSearchHref;
      $[46] = myHref;
      $[47] = openKakaobankExit;
      $[48] = searchButton;
      $[49] = t18;
    } else {
      t18 = $[49];
    }
    let t19;
    if ($[50] !== t18) {
      t19 = <div className="z-[1] flex shrink-0 items-center">{t18}</div>;
      $[50] = t18;
      $[51] = t19;
    } else {
      t19 = $[51];
    }
    let t20;
    if ($[52] !== closeKakaobankExit || $[53] !== handleKakaobankClose || $[54] !== isKakaobankExitOpened) {
      t20 = <KakaobankExitConfirmModal opened={isKakaobankExitOpened} onClose={closeKakaobankExit} onConfirm={handleKakaobankClose} />;
      $[52] = closeKakaobankExit;
      $[53] = handleKakaobankClose;
      $[54] = isKakaobankExitOpened;
      $[55] = t20;
    } else {
      t20 = $[55];
    }
    let t21;
    if ($[56] !== style || $[57] !== t12 || $[58] !== t14 || $[59] !== t17 || $[60] !== t19 || $[61] !== t20) {
      t21 = <nav aria-label={"\uD398\uC774\uC9C0 \uB0B4\uBE44\uAC8C\uC774\uC158"} className={t12} style={style}>{t14}{t17}{t19}{t20}</nav>;
      $[56] = style;
      $[57] = t12;
      $[58] = t14;
      $[59] = t17;
      $[60] = t19;
      $[61] = t20;
      $[62] = t21;
    } else {
      t21 = $[62];
    }
    return t21;
  }
  let t6;
  if ($[63] !== className || $[64] !== partnerChromeClassName) {
    t6 = cn("relative flex min-h-48 w-full items-center", "box-border select-none bg-Gray-white text-center", partnerChromeClassName, className);
    $[63] = className;
    $[64] = partnerChromeClassName;
    $[65] = t6;
  } else {
    t6 = $[65];
  }
  let t7;
  if ($[66] !== textColor || $[67] !== titleLeft) {
    t7 = titleLeft ? <h3 className={cn("absolute left-20 font-700 flex-center typo-h6", textColor)}>{titleLeft}</h3> : null;
    $[66] = textColor;
    $[67] = titleLeft;
    $[68] = t7;
  } else {
    t7 = $[68];
  }
  let t8;
  if ($[69] !== textColor || $[70] !== title) {
    t8 = title ? <h3 className={cn("absolute flex size-full items-center justify-center", "break-all text-center font-600 text-Gray-900 typo-body3", textColor)}>{title}</h3> : null;
    $[69] = textColor;
    $[70] = title;
    $[71] = t8;
  } else {
    t8 = $[71];
  }
  let t9;
  if ($[72] !== DEFAULT_LOGO_HREF || $[73] !== isToss || $[74] !== logo || $[75] !== logoHref) {
    t9 = logo ? <div className="flex items-center gap-12"><div className={cn("flex items-center gap-12", {
        "gap-2": isToss
      })}>{isToss ? <><Image src={TOSS_LOGO_URL} alt="toss" width={31} height={31} /><SvgDelete className="size-20 text-Gray-400" /></> : null}<CustomLink prefetch={false} aria-label={"\uC6F0\uB85C"} href={logoHref ?? DEFAULT_LOGO_HREF} {...gtmDatasets.clickGnb({
          menu_name: "\uB85C\uACE0"
        })}><SvgLogo className="h-29 w-86 text-Gray-black" /></CustomLink></div></div> : null;
    $[72] = DEFAULT_LOGO_HREF;
    $[73] = isToss;
    $[74] = logo;
    $[75] = logoHref;
    $[76] = t9;
  } else {
    t9 = $[76];
  }
  let t10;
  if ($[77] !== backButton || $[78] !== backButtonType || $[79] !== backFallbackHref || $[80] !== handleBack || $[81] !== textColor) {
    t10 = backButton ? <Button asChild={true} className="relative size-26 p-0 before:absolute before:-inset-9 before:content-['']" dimensions="H26" theme="primary-text"><a aria-label={"\uB4A4\uB85C \uAC00\uAE30"} href={backFallbackHref} onClick={event => {
        event.preventDefault();
        handleBack();
      }}>{backButtonType === "back" ? <SvgChevronleft className={cn("size-24", textColor)} /> : <SvgDelete className={cn("size-24", textColor)} />}</a></Button> : null;
    $[77] = backButton;
    $[78] = backButtonType;
    $[79] = backFallbackHref;
    $[80] = handleBack;
    $[81] = textColor;
    $[82] = t10;
  } else {
    t10 = $[82];
  }
  let t11;
  if ($[83] !== hasBottomNav || $[84] !== hideHomeButton || $[85] !== isNotUsedHomeButton || $[86] !== router || $[87] !== textColor) {
    t11 = !hasBottomNav && !isNotUsedHomeButton ? <Button aria-label={"\uD648\uC73C\uB85C \uAC00\uAE30"} className={cn("relative size-26 p-0", hideHomeButton && "hidden")} dimensions="H26" theme="primary-text" onClick={() => router.push(ROUTES.HOME.pathname)}><SvgHome className={cn("size-24", textColor)} /></Button> : null;
    $[83] = hasBottomNav;
    $[84] = hideHomeButton;
    $[85] = isNotUsedHomeButton;
    $[86] = router;
    $[87] = textColor;
    $[88] = t11;
  } else {
    t11 = $[88];
  }
  let t12;
  if ($[89] !== donationHomeButton || $[90] !== router || $[91] !== textColor) {
    t12 = donationHomeButton ? <Button aria-label={"\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uD648\uC73C\uB85C \uAC00\uAE30"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={() => router.push(DONATION_ROUTES.HOME.pathname)}><SvgHome className={cn("size-24", textColor)} /></Button> : null;
    $[89] = donationHomeButton;
    $[90] = router;
    $[91] = textColor;
    $[92] = t12;
  } else {
    t12 = $[92];
  }
  let t13;
  if ($[93] !== benefitHomeButton || $[94] !== router || $[95] !== textColor) {
    t13 = benefitHomeButton ? <Button aria-label={"\uD61C\uD0DD\uD648\uC73C\uB85C \uAC00\uAE30"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={() => router.push(ROUTES.BENEFIT.pathname)}><SvgHome className={cn("size-24", textColor)} /></Button> : null;
    $[93] = benefitHomeButton;
    $[94] = router;
    $[95] = textColor;
    $[96] = t13;
  } else {
    t13 = $[96];
  }
  let t14;
  if ($[97] !== onSearchFormClick || $[98] !== onSearchFormMount || $[99] !== onSearchFormSubmit || $[100] !== searchForm || $[101] !== searchFormClassName || $[102] !== searchFormDefaultValue) {
    t14 = searchForm ? <Input ref={el => {
      if (!el) {
        return;
      }
      onSearchFormMount?.(el);
    }} key={searchFormDefaultValue} type="search" placeholder={"\uAC80\uC0C9\uC5B4\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694"} dimensions="H40" defaultValue={searchFormDefaultValue} className={cn("my-12 w-full select-text border-none bg-Gray-200 text-Gray-900 placeholder:text-Gray-550 focus:border-none", searchFormClassName)} onClick={() => onSearchFormClick?.()} onKeyDown={e => {
      if (e.key === "Enter") {
        onSearchFormSubmit?.(e.currentTarget.value);
      }
    }} onClear={() => onSearchFormSubmit?.("")} /> : null;
    $[97] = onSearchFormClick;
    $[98] = onSearchFormMount;
    $[99] = onSearchFormSubmit;
    $[100] = searchForm;
    $[101] = searchFormClassName;
    $[102] = searchFormDefaultValue;
    $[103] = t14;
  } else {
    t14 = $[103];
  }
  let t15;
  if ($[104] !== t10 || $[105] !== t11 || $[106] !== t12 || $[107] !== t13 || $[108] !== t14 || $[109] !== t9) {
    t15 = <div className="z-[1] flex min-w-0 grow items-center gap-20 pl-20">{t9}{t10}{t11}{t12}{t13}{t14}</div>;
    $[104] = t10;
    $[105] = t11;
    $[106] = t12;
    $[107] = t13;
    $[108] = t14;
    $[109] = t9;
    $[110] = t15;
  } else {
    t15 = $[110];
  }
  let t16;
  if ($[111] !== searchButton || $[112] !== textColor) {
    t16 = searchButton ? <Button aria-label={"\uAC80\uC0C9\uC73C\uB85C \uC774\uB3D9"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" asChild={true} {...gtmDatasets.clickGnb({
      menu_name: "\uAC80\uC0C9"
    })}><CustomLink prefetch={false} href={typeof searchButton === "boolean" ? ROUTES.SEARCH.pathname : searchButton}><SvgMagnifyingGlass className={cn("size-24", textColor)} /></CustomLink></Button> : null;
    $[111] = searchButton;
    $[112] = textColor;
    $[113] = t16;
  } else {
    t16 = $[113];
  }
  let t17;
  if ($[114] !== bellButton || $[115] !== notification || $[116] !== router || $[117] !== textColor) {
    t17 = bellButton ? <Button aria-label={"\uC54C\uB9BC\uC73C\uB85C \uC774\uB3D9"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={() => {
      router.push(ROUTES.PUSH.pathname);
    }}><SvgNotification className={cn("size-24", textColor)} />{notification ? <span className="absolute right-14 top-12 box-content size-4 rounded-pill border-2 border-Gray-100 bg-R-500" /> : null}</Button> : null;
    $[114] = bellButton;
    $[115] = notification;
    $[116] = router;
    $[117] = textColor;
    $[118] = t17;
  } else {
    t17 = $[118];
  }
  let t18;
  if ($[119] !== shareButton || $[120] !== shareContent || $[121] !== textColor) {
    t18 = shareButton ? <Button aria-label={"\uACF5\uC720\uD558\uAE30"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={() => {
      openShareDrawer(location.href, shareContent);
    }}><SvgExpandWindow className={cn("size-24", textColor)} /></Button> : null;
    $[119] = shareButton;
    $[120] = shareContent;
    $[121] = textColor;
    $[122] = t18;
  } else {
    t18 = $[122];
  }
  let t19;
  if ($[123] !== router || $[124] !== settingsButton || $[125] !== textColor) {
    t19 = settingsButton ? <Button aria-label={"\uC124\uC815"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={() => router.push(ROUTES.MY_SETTINGS.pathname)}><SvgSetting className={cn("size-24", textColor)} /></Button> : null;
    $[123] = router;
    $[124] = settingsButton;
    $[125] = textColor;
    $[126] = t19;
  } else {
    t19 = $[126];
  }
  let t20;
  if ($[127] !== kebabButton || $[128] !== onClickKebab || $[129] !== textColor) {
    t20 = kebabButton ? typeof kebabButton === "boolean" ? <Button aria-label={"\uB354\uBCF4\uAE30"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={onClickKebab}><SvgKebab className={cn("size-24", textColor)} /></Button> : kebabButton : null;
    $[127] = kebabButton;
    $[128] = onClickKebab;
    $[129] = textColor;
    $[130] = t20;
  } else {
    t20 = $[130];
  }
  let t21;
  if ($[131] !== closeConfirmModal || $[132] !== confirmAndClose || $[133] !== isModalOpened || $[134] !== isToss || $[135] !== openConfirmModal) {
    t21 = isToss ? <><Button aria-label={"\uD1A0\uC2A4\uB85C \uC774\uB3D9"} className="relative h-26 whitespace-nowrap p-0" dimensions="H40" theme="primary-text" onClick={openConfirmModal}>닫기</Button><TossExitConfirmModal opened={isModalOpened} onClose={closeConfirmModal} onConfirm={confirmAndClose} /></> : null;
    $[131] = closeConfirmModal;
    $[132] = confirmAndClose;
    $[133] = isModalOpened;
    $[134] = isToss;
    $[135] = openConfirmModal;
    $[136] = t21;
  } else {
    t21 = $[136];
  }
  let t22;
  if ($[137] !== children || $[138] !== t16 || $[139] !== t17 || $[140] !== t18 || $[141] !== t19 || $[142] !== t20 || $[143] !== t21) {
    t22 = <div className="z-[1] ml-auto flex shrink-0 items-center justify-end gap-20 px-20">{t16}{t17}{children}{t18}{t19}{t20}{t21}</div>;
    $[137] = children;
    $[138] = t16;
    $[139] = t17;
    $[140] = t18;
    $[141] = t19;
    $[142] = t20;
    $[143] = t21;
    $[144] = t22;
  } else {
    t22 = $[144];
  }
  let t23;
  if ($[145] !== style || $[146] !== t15 || $[147] !== t22 || $[148] !== t6 || $[149] !== t7 || $[150] !== t8) {
    t23 = <nav aria-label={"\uD398\uC774\uC9C0 \uB0B4\uBE44\uAC8C\uC774\uC158"} className={t6} style={style}>{t7}{t8}{t15}{t22}</nav>;
    $[145] = style;
    $[146] = t15;
    $[147] = t22;
    $[148] = t6;
    $[149] = t7;
    $[150] = t8;
    $[151] = t23;
  } else {
    t23 = $[151];
  }
  return t23;
};
export interface PageNavigationProps {
  className?: string;
  title?: string | ReactNode;
  titleLeft?: string | ReactNode;
  children?: ReactNode;
  logo?: boolean;
  logoHref?: string;
  searchButton?: boolean | string;
  bellButton?: boolean;
  shareButton?: boolean;
  //* 없으면 앱이 웹뷰 DOM의 <title>·description을 읽어 링크 미리보기(og)와 문구가 달라진다
  shareContent?: ShareContent;
  notification?: boolean;
  settingsButton?: boolean;
  backButton?: boolean;
  backButtonType?: 'exit' | 'back';
  /** backButton 유무와 관계없이 뒤로가기 활성화 */
  enableHistoryBack?: boolean;
  kebabButton?: boolean | ReactNode;
  onClickKebab?: VoidFunction;
  hideHomeButton?: boolean;
  onClickBack?: VoidFunction | string;
  //* 되감을 히스토리가 없을 때(딥링크·푸시 진입) 뒤로가기가 향할 허브 — 헤더·앱 하드웨어 back 공통
  fallbackHref?: string;
  style?: React.CSSProperties;
  textColor?: string;
  isNotUsedHomeButton?: boolean;
  donationHomeButton?: boolean;
  benefitHomeButton?: boolean;
  //* 검색 폼 관련
  searchForm?: boolean;
  searchFormDefaultValue?: string;
  searchFormClassName?: string;
  onSearchFormMount?: (ref: HTMLInputElement) => void;
  onSearchFormClick?: () => void;
  onSearchFormSubmit?: (value: string) => void;
}
export const PageNavigation = props => {
  const $ = _c(76);
  const {
    logo,
    logoHref,
    title,
    titleLeft,
    children,
    backButton: t0,
    backButtonType: t1,
    enableHistoryBack,
    searchButton,
    bellButton,
    notification,
    shareButton,
    shareContent,
    settingsButton,
    kebabButton,
    onClickBack,
    fallbackHref,
    onClickKebab,
    hideHomeButton,
    className,
    style,
    textColor,
    isNotUsedHomeButton,
    donationHomeButton,
    benefitHomeButton,
    searchForm,
    searchFormDefaultValue,
    searchFormClassName,
    onSearchFormMount,
    onSearchFormClick,
    onSearchFormSubmit
  } = props;
  const backButton = t0 === undefined ? true : t0;
  const backButtonType = t1 === undefined ? "back" : t1;
  const router = useCustomRouter();
  const {
    openConfirmModal,
    closeConfirmModal,
    confirmAndClose,
    isModalOpened
  } = useTossWebView();
  const [isKbWebview, setAppModal, isToss, isWelloApp] = useDeviceInfoStore(useShallow(_temp2));
  const isKakaobank = useCheckKakaobank();
  const isLogin = useAuthStore(_temp3);
  let t2;
  if ($[0] !== backButton || $[1] !== enableHistoryBack || $[2] !== fallbackHref || $[3] !== router || $[4] !== setAppModal) {
    t2 = () => {
      const _window = window as FlutterInAppWebViewWindow;
      if (_window.router) {
        _window.router.back = backButton || enableHistoryBack ? () => router.back(fallbackHref) : () => {
          if (useResponsiveDialogStore.getState().isOpen || hasOverlayHistoryEntry()) {
            router.back(fallbackHref);
            return;
          }
          setAppModal({
            isOpened: true,
            type: "exit"
          });
        };
        return () => {
          _window.router.back = () => router.back();
        };
      }
    };
    $[0] = backButton;
    $[1] = enableHistoryBack;
    $[2] = fallbackHref;
    $[3] = router;
    $[4] = setAppModal;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== backButton || $[7] !== enableHistoryBack || $[8] !== fallbackHref || $[9] !== isWelloApp || $[10] !== router || $[11] !== setAppModal) {
    t3 = [backButton, enableHistoryBack, fallbackHref, isWelloApp, router, setAppModal];
    $[6] = backButton;
    $[7] = enableHistoryBack;
    $[8] = fallbackHref;
    $[9] = isWelloApp;
    $[10] = router;
    $[11] = setAppModal;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  useEffect(t2, t3);
  const pathname = useRenderedPathname();
  const stickyHeaderRef = useRef(null);
  usePublishElementHeight(stickyHeaderRef, "--mobile-header-height");
  const shouldHideNavigation = isKbWebview && pathname !== MODULE_ROUTES.KB_HOME.pathname;
  let t4;
  if ($[13] !== backButton || $[14] !== backButtonType || $[15] !== bellButton || $[16] !== benefitHomeButton || $[17] !== children || $[18] !== className || $[19] !== closeConfirmModal || $[20] !== confirmAndClose || $[21] !== donationHomeButton || $[22] !== enableHistoryBack || $[23] !== fallbackHref || $[24] !== hideHomeButton || $[25] !== isKakaobank || $[26] !== isLogin || $[27] !== isModalOpened || $[28] !== isNotUsedHomeButton || $[29] !== isToss || $[30] !== kebabButton || $[31] !== logo || $[32] !== logoHref || $[33] !== notification || $[34] !== onClickBack || $[35] !== onClickKebab || $[36] !== onSearchFormClick || $[37] !== onSearchFormMount || $[38] !== onSearchFormSubmit || $[39] !== openConfirmModal || $[40] !== pathname || $[41] !== router || $[42] !== searchButton || $[43] !== searchForm || $[44] !== searchFormClassName || $[45] !== searchFormDefaultValue || $[46] !== settingsButton || $[47] !== shareButton || $[48] !== shareContent || $[49] !== style || $[50] !== textColor || $[51] !== title || $[52] !== titleLeft) {
    t4 = {
      logo,
      logoHref,
      title,
      titleLeft,
      children,
      backButton,
      backButtonType,
      enableHistoryBack,
      searchButton,
      bellButton,
      notification,
      shareButton,
      shareContent,
      settingsButton,
      kebabButton,
      onClickBack,
      fallbackHref,
      onClickKebab,
      hideHomeButton,
      className,
      style,
      textColor,
      isNotUsedHomeButton,
      donationHomeButton,
      benefitHomeButton,
      searchForm,
      searchFormDefaultValue,
      searchFormClassName,
      onSearchFormMount,
      onSearchFormClick,
      onSearchFormSubmit,
      isToss,
      isKakaobank,
      isLogin,
      pathname,
      router,
      openConfirmModal,
      closeConfirmModal,
      confirmAndClose,
      isModalOpened
    };
    $[13] = backButton;
    $[14] = backButtonType;
    $[15] = bellButton;
    $[16] = benefitHomeButton;
    $[17] = children;
    $[18] = className;
    $[19] = closeConfirmModal;
    $[20] = confirmAndClose;
    $[21] = donationHomeButton;
    $[22] = enableHistoryBack;
    $[23] = fallbackHref;
    $[24] = hideHomeButton;
    $[25] = isKakaobank;
    $[26] = isLogin;
    $[27] = isModalOpened;
    $[28] = isNotUsedHomeButton;
    $[29] = isToss;
    $[30] = kebabButton;
    $[31] = logo;
    $[32] = logoHref;
    $[33] = notification;
    $[34] = onClickBack;
    $[35] = onClickKebab;
    $[36] = onSearchFormClick;
    $[37] = onSearchFormMount;
    $[38] = onSearchFormSubmit;
    $[39] = openConfirmModal;
    $[40] = pathname;
    $[41] = router;
    $[42] = searchButton;
    $[43] = searchForm;
    $[44] = searchFormClassName;
    $[45] = searchFormDefaultValue;
    $[46] = settingsButton;
    $[47] = shareButton;
    $[48] = shareContent;
    $[49] = style;
    $[50] = textColor;
    $[51] = title;
    $[52] = titleLeft;
    $[53] = t4;
  } else {
    t4 = $[53];
  }
  const contentProps = t4;
  if (shouldHideNavigation) {
    return null;
  }
  const navHeightClass = searchForm && !isKakaobank ? "h-64" : "h-48";
  if (!shouldShowMobileTrendKeyword(pathname)) {
    let t5;
    if ($[54] !== navHeightClass) {
      t5 = cn("hidden mobile-tablet:block", navHeightClass);
      $[54] = navHeightClass;
      $[55] = t5;
    } else {
      t5 = $[55];
    }
    let t6;
    if ($[56] !== t5) {
      t6 = <div aria-hidden={true} className={t5} />;
      $[56] = t5;
      $[57] = t6;
    } else {
      t6 = $[57];
    }
    let t7;
    if ($[58] !== className || $[59] !== navHeightClass) {
      t7 = cn("fixed inset-x-0 top-0 z-20 hidden mobile-tablet:flex", navHeightClass, className);
      $[58] = className;
      $[59] = navHeightClass;
      $[60] = t7;
    } else {
      t7 = $[60];
    }
    let t8;
    if ($[61] !== contentProps || $[62] !== t7) {
      t8 = <PageNavigationContent {...contentProps} className={t7} />;
      $[61] = contentProps;
      $[62] = t7;
      $[63] = t8;
    } else {
      t8 = $[63];
    }
    let t9;
    if ($[64] !== t6 || $[65] !== t8) {
      t9 = <>{t6}{t8}</>;
      $[64] = t6;
      $[65] = t8;
      $[66] = t9;
    } else {
      t9 = $[66];
    }
    return t9;
  }
  let t5;
  if ($[67] !== className) {
    t5 = cn("flex", className);
    $[67] = className;
    $[68] = t5;
  } else {
    t5 = $[68];
  }
  let t6;
  if ($[69] !== contentProps || $[70] !== t5) {
    t6 = <PageNavigationContent {...contentProps} className={t5} />;
    $[69] = contentProps;
    $[70] = t5;
    $[71] = t6;
  } else {
    t6 = $[71];
  }
  let t7;
  let t8;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TrendKeywordBar />;
    t8 = <div id={SELECTOR.MOBILE_HEADER_ATTACHMENT} className="absolute inset-x-0 top-full" />;
    $[72] = t7;
    $[73] = t8;
  } else {
    t7 = $[72];
    t8 = $[73];
  }
  let t9;
  if ($[74] !== t6) {
    t9 = <div ref={stickyHeaderRef} className="group/header sticky top-0 z-20 hidden mobile-tablet:block">{t6}{t7}{t8}</div>;
    $[74] = t6;
    $[75] = t9;
  } else {
    t9 = $[75];
  }
  return t9;
};
function _temp(state) {
  return [state.isExitConfirmOpened, state.openExitConfirm, state.closeExitConfirm];
}
function _temp2(state) {
  return [state.webviewType === WEBVIEW_TYPE.KB_PAY, state.setAppModal, state.subDomain === SUB_DOMAIN.TOSS, state.webviewType === WEBVIEW_TYPE.WELLO_APP] as const;
}
function _temp3(state_0) {
  return state_0.isLogin;
}